import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Save, Shield, AlertCircle, CheckCircle, Key, Globe, Webhook, Smartphone } from 'lucide-react';
import { supabase } from '../../supabase/client';

interface AlipayConfig {
  id: string;
  app_id: string;
  private_key: string;
  alipay_public_key: string;
  sandbox_mode: boolean;
  notify_url: string;
  return_url: string;
  is_active: boolean;
  updated_at: string;
}

export default function AlipayConfigPage() {
  const [config, setConfig] = useState<AlipayConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState({
    app_id: '',
    private_key: '',
    alipay_public_key: '',
    sandbox_mode: true,
    notify_url: '',
    return_url: '',
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('alipay_config')
        .select('*')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        
        setMessage({ type: 'error', text: '获取配置失败: ' + error.message });
      } else if (data) {
        setConfig(data);
        setFormData({
          app_id: data.app_id || '',
          private_key: data.private_key || '',
          alipay_public_key: data.alipay_public_key || '',
          sandbox_mode: data.sandbox_mode ?? true,
          notify_url: data.notify_url || '',
          return_url: data.return_url || '',
        });
      }
    } catch (err: any) {
      
      setMessage({ type: 'error', text: '获取配置异常: ' + err.message });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const saveData = {
        app_id: formData.app_id,
        private_key: formData.private_key,
        alipay_public_key: formData.alipay_public_key,
        sandbox_mode: formData.sandbox_mode,
        notify_url: formData.notify_url,
        return_url: formData.return_url,
        is_active: true,
        updated_at: new Date().toISOString()
      };

      let result;
      if (config?.id) {
        result = await supabase
          .from('alipay_config')
          .update(saveData)
          .eq('id', config.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from('alipay_config')
          .insert(saveData)
          .select()
          .single();
      }

      if (result.error) {
        throw result.error;
      }

      setMessage({ type: 'success', text: '配置保存成功' });
      await fetchConfig();
    } catch (err: any) {
      
      setMessage({ type: 'error', text: '保存失败: ' + (err.message || '未知错误') });
    }
    setSaving(false);
  };

  const maskKey = (key: string) => key ? key.slice(0, 20) + '...' + key.slice(-10) : '';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">支付宝配置</h1>
          <p className="text-sm text-gray-500 mt-1">管理支付宝支付参数，支持沙箱/生产环境切换</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            formData.sandbox_mode ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
          }`}>
            {formData.sandbox_mode ? '沙箱环境' : '生产环境'}
          </span>
        </div>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-gray-200 p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">基础配置</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">应用ID (app_id)</label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.app_id}
                    onChange={(e) => setFormData({ ...formData, app_id: e.target.value })}
                    placeholder="2024XXXXXXXXXXXX"
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">应用私钥 (private_key)</label>
                <textarea
                  value={formData.private_key}
                  onChange={(e) => setFormData({ ...formData, private_key: e.target.value })}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
                />
                {config?.private_key && !formData.private_key.startsWith('-----') && (
                  <p className="text-xs text-gray-500 mt-1">已配置: {maskKey(config.private_key)}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">支付宝公钥 (alipay_public_key)</label>
                <textarea
                  value={formData.alipay_public_key}
                  onChange={(e) => setFormData({ ...formData, alipay_public_key: e.target.value })}
                  placeholder="-----BEGIN PUBLIC KEY-----"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl border border-gray-200 p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Webhook className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">回调配置</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">异步通知地址 (notify_url)</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.notify_url}
                    onChange={(e) => setFormData({ ...formData, notify_url: e.target.value })}
                    placeholder="https://your-domain.com/api/alipay/notify"
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">同步返回地址 (return_url)</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.return_url}
                    onChange={(e) => setFormData({ ...formData, return_url: e.target.value })}
                    placeholder="https://your-domain.com/payment-result"
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-gray-200 p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">环境切换</h2>
            </div>

            <div className="space-y-4">
              <div className={`p-4 rounded-lg border-2 transition-colors ${
                formData.sandbox_mode ? 'border-amber-500 bg-amber-50' : 'border-gray-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">沙箱环境</p>
                    <p className="text-sm text-gray-500">用于开发测试</p>
                  </div>
                  <button
                    onClick={() => setFormData({ ...formData, sandbox_mode: true })}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      formData.sandbox_mode ? 'border-amber-500 bg-amber-500' : 'border-gray-300'
                    }`}
                  >
                    {formData.sandbox_mode && <CheckCircle className="w-4 h-4 text-white" />}
                  </button>
                </div>
              </div>

              <div className={`p-4 rounded-lg border-2 transition-colors ${
                !formData.sandbox_mode ? 'border-green-500 bg-green-50' : 'border-gray-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">生产环境</p>
                    <p className="text-sm text-gray-500">真实支付环境</p>
                  </div>
                  <button
                    onClick={() => setFormData({ ...formData, sandbox_mode: false })}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      !formData.sandbox_mode ? 'border-green-500 bg-green-500' : 'border-gray-300'
                    }`}
                  >
                    {!formData.sandbox_mode && <CheckCircle className="w-4 h-4 text-white" />}
                  </button>
                </div>
              </div>

              {!formData.sandbox_mode && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-700">切换到生产环境后，将使用真实资金进行交易</p>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-6 text-white"
          >
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5" />
              <h3 className="font-semibold">安全提示</h3>
            </div>
            <ul className="space-y-2 text-sm text-white/80">
              <li>• 私钥仅存储在服务端</li>
              <li>• 支持RSA2签名验证</li>
              <li>• 回调通知自动验签</li>
              <li>• 敏感信息已脱敏显示</li>
            </ul>
          </motion.div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                保存中...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                保存配置
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
